import * as React from "react"
import { Button, Badge, CardHeader, Spacer, ThemeCss } from "gatsby-interface"
import { MdArrowForward } from "react-icons/md"
import { GatsbyCloudLogo } from "@modules/brand/assets/GatsbyCloudLogo"
import {
  gatsbyCloudCdnIntegrationConfig as text,
  ui as uiText,
} from "@modules/locales/default.js"
import { DisableCloudCdnForm } from "./DisableCloudCdnForm"
import { HostingDetails } from "@modules/graphql/types"

enum ConnectState {
  Connected = `CONNECTED`,
  NotConnected = `NOT_CONNECTED`,
  Connecting = `CONNECTING`,
}

const headerCss: ThemeCss = theme => ({
  alignItems: `flex-start`,
  flexWrap: `wrap`,
  flexDirection: `column`,

  [theme.mediaQueries.tablet]: {
    justifyContent: `flex-start`,
    flexDirection: `row`,
  },
})

const titleCss: ThemeCss = _theme => ({
  alignItems: `center`,
  display: `flex`,
  flexShrink: 0,
  flexWrap: `wrap`,
})

const logoCss: ThemeCss = theme => ({
  width: theme.fontSizes[8],
  height: `auto`,
  marginRight: theme.space[3],
  marginLeft: `-${theme.space[2]}`,
  flexShrink: 0,
})

const statusCss: ThemeCss = theme => ({
  flexGrow: 1,
  display: `flex`,
  marginTop: theme.space[3],

  [theme.mediaQueries.tablet]: {
    justifyContent: `space-between`,
    marginLeft: theme.space[6],
    marginTop: 0,
  },
})

type ActionFunction = () => void

export type GatsbyCloudCardHeaderProps = {
  siteName: string
  isConnected: boolean
  connectState: ConnectState
  enableCloudCdn: ActionFunction
  disableCloudCdn: ActionFunction
  allowToEdit?: boolean
  hostingDetails?: HostingDetails
}

export function GatsbyCloudCardHeader({
  siteName,
  isConnected,
  connectState,
  enableCloudCdn,
  disableCloudCdn,
  hostingDetails,
  allowToEdit,
}: GatsbyCloudCardHeaderProps) {
  const [
    disableCloudCdnRequested,
    setDisableCloudCdnRequested,
  ] = React.useState<boolean>(false)

  return (
    <CardHeader
      css={headerCss}
      title={
        <div css={titleCss}>
          <GatsbyCloudLogo css={logoCss} />
          {text.headers.setupGatsbyCloudCdn}
        </div>
      }
    >
      <div css={statusCss}>
        <Badge tone={isConnected ? `SUCCESS` : `NEUTRAL`} size="M">
          {connectState === ConnectState.NotConnected &&
            text.labels.notConnected}
          {connectState === ConnectState.Connected && text.labels.connected}
          {connectState === ConnectState.Connecting && text.labels.connecting}
        </Badge>
        <Spacer size={5} direction="horizontal" />
        {allowToEdit && (
          <React.Fragment>
            {connectState === ConnectState.Connected ? (
              <Button
                variant="GHOST"
                onClick={() => setDisableCloudCdnRequested(true)}
                loadingLabel=""
              >
                {uiText.actions.turnOff}
              </Button>
            ) : (
              <Button
                variant="GHOST"
                rightIcon={<MdArrowForward />}
                onClick={enableCloudCdn}
                disabled={connectState === ConnectState.Connecting}
                loading={connectState === ConnectState.Connecting}
                loadingLabel=""
              >
                {uiText.actions.turnOn}
              </Button>
            )}
          </React.Fragment>
        )}
      </div>

      {disableCloudCdnRequested && (
        <DisableCloudCdnForm
          siteName={siteName}
          onDismiss={() => setDisableCloudCdnRequested(false)}
          onDisable={disableCloudCdn}
          disableRequested={disableCloudCdnRequested}
          hostingDetails={hostingDetails}
        />
      )}
    </CardHeader>
  )
}
